import { Node } from "@xyflow/react";

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BadgePosition {
  x: number;
  y: number;
}

export interface BadgeDimensions {
  width: number;
  height: number;
}

const BADGE_PADDING = 8;

export const DEFAULT_BADGE_DIMENSIONS: BadgeDimensions = {
  width: 120,
  height: 24,
};

export function measureBadgeDimensions(
  badgeElement: HTMLElement,
  zoom: number,
): BadgeDimensions {
  const rect = badgeElement.getBoundingClientRect();

  return { width: rect.width / zoom, height: rect.height / zoom };
}

export function getNodeBoundingBox(node: Node): BoundingBox {
  const width = node.data.stepId === "start" ? 156 : 224;
  const height = 100;

  return { x: node.position.x, y: node.position.y, width, height };
}

export function getBadgeBoundingBox(
  position: BadgePosition,
  dimensions: BadgeDimensions,
): BoundingBox {
  return {
    x: position.x - dimensions.width / 2 - BADGE_PADDING,
    y: position.y - dimensions.height / 2 - BADGE_PADDING,
    width: dimensions.width + BADGE_PADDING * 2,
    height: dimensions.height + BADGE_PADDING * 2,
  };
}

export function checkOverlap(box1: BoundingBox, box2: BoundingBox): boolean {
  return !(
    box1.x + box1.width < box2.x ||
    box2.x + box2.width < box1.x ||
    box1.y + box1.height < box2.y ||
    box2.y + box2.height < box1.y
  );
}

export function checkBadgeOverlapsWithNodes(
  badgePosition: BadgePosition,
  nodes: Node[],
  dimensions: BadgeDimensions,
): boolean {
  const badgeBox = getBadgeBoundingBox(badgePosition, dimensions);

  for (const node of nodes) {
    const nodeBox = getNodeBoundingBox(node);
    if (checkOverlap(badgeBox, nodeBox)) {
      return true;
    }
  }

  return false;
}

export function checkBadgeOverlapsWithOtherBadges(
  badgePosition: BadgePosition,
  existingBadgePositions: BadgePosition[],
  dimensions: BadgeDimensions,
): boolean {
  const newBadgeBox = getBadgeBoundingBox(badgePosition, dimensions);

  for (const existingPosition of existingBadgePositions) {
    const existingBadgeBox = getBadgeBoundingBox(existingPosition, dimensions);
    if (checkOverlap(newBadgeBox, existingBadgeBox)) {
      return true;
    }
  }

  return false;
}
